import { Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { GridCellParams, GridColDef, GridSortModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getSpecificMemberChangeLog } from 'src/shared/services/memberChangeLogService';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { MemberChangeLog } from '../models/memberChangeLog';

type MemberRecentChangeInfoDataGridProps = {
  memberId: number;
};

export const MemberRecentChangeInfoDataGrid = (props: MemberRecentChangeInfoDataGridProps) => {
  const { memberId } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { dateStamp } = useGlobals();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'date', sort: 'desc' }]);
  const [rows, setRows] = useState<MemberChangeLog[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: false,
    totalRows: 0,
    rowsPerPageOptions: [10, 25, 50, 100],
    pageSize: 10 || 25,
    page: 0,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    updateData('loading', true);
    try {
      if (memberId) {
        const res = await getSpecificMemberChangeLog(data.page, data.pageSize, sortModel[0].field, sortModel[0].sort?.toString(), memberId);
        if (isMounted()) {
          updateData('totalRows', res.data.totalItems);
          setRows(res.data.results);
        }
      }
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log(error, dateStamp);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        setIsLoading(false);
      }
    }
  }, [data.page, data.pageSize, dateStamp, isMounted, memberId, sortModel]);

  useEffect(() => {
    if (memberId) {
      fetchData();
    }
  }, [fetchData, memberId]);

  const columns: GridColDef[] = [
    {
      field: 'date',
      flex: 0.7,
      sortable: false,
      headerName: t('member:tableHeaders.date'),
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDate(params.row.date)}</>;
      },
    },
    {
      field: 'action',
      flex: 1.3,
      sortable: false,
      headerName: t('member:tableHeaders.action'),
      renderCell: (params: GridCellParams) => {
        return <>{params.row.action}</>;
      },
    },

    {
      field: 'data',
      flex: 3,
      sortable: false,
      headerName: t('member:tableHeaders.description'),
      renderCell: (params: GridCellParams) => {
        return <>{params.row.data}</>;
      },
    },
    {
      field: 'signature',
      flex: 1,
      sortable: false,
      headerName: t('member:tableHeaders.signature'),
      renderCell: (params: GridCellParams) => {
        return <>{params.row.signature}</>;
      },
    },
  ];

  return (
    <>
      {rows && (
        <Box>
          <FormLoaderWrapper loading={isLoading}>
            <DataGrid<MemberChangeLog>
              getRowId={(row) => row.changeLog_id}
              autoHeight
              columns={columns}
              rows={rows}
              dataModel={data}
              disableSelectionOnClick
              disablePointer
              checkboxSelection={false}
              onSelectionModelChange={(newSelectionModel) => {
                setSelectionModel(newSelectionModel);
                ConsoleHelper.log();
              }}
              sortModel={sortModel}
              onSortModelChange={(newSortModel) => {
                ConsoleHelper.log('newSortModel::', newSortModel);
                if (newSortModel.length) {
                  setSortModel(newSortModel);
                } else {
                  setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
                }
              }}
              selectionModel={selectionModel}
              onPageChange={(val) => setData((prev) => ({ ...prev, page: val }))}
              onPageSizeChange={(val) => setData((prev) => ({ ...prev, pageSize: val }))}
            />
          </FormLoaderWrapper>
        </Box>
      )}
    </>
  );
};
